import { useMemo } from 'react'
import {
    CalendarView,
    getWeekDays,
    navigateDate,
} from '../../utils/calendar-utils'
import { AgendaEvent } from '@/lib/api/common/interfaces/agenda.interface'
import { useCalendar } from '../../hooks/useCalendar'
import { CalendarHeader } from './CalendarHeader'
import { MonthView } from './MonthView'
import { WeekView } from './WeekView'
import { DayView } from './DayView'

interface CustomCalendarProps {
    events: AgendaEvent[]
    initialDate?: Date
    initialView?: CalendarView
    workingHours?: { start: number; end: number }
    slotDuration?: number
    onDateChange?: (date: Date) => void
    onViewChange?: (view: CalendarView) => void
    onSlotClick?: (date: Date) => void
    onEventClick?: (event: AgendaEvent) => void
}

export function CustomCalendar({
    events,
    initialDate,
    initialView = 'month',
    workingHours = { start: 8, end: 18 },
    slotDuration = 30,
    onDateChange,
    onViewChange,
    onSlotClick,
    onEventClick,
}: CustomCalendarProps) {
    const calendar = useCalendar({ initialDate, initialView })

    const handleViewChange = (view: CalendarView) => {
        calendar.setView(view)
        if (onViewChange) {
            onViewChange(view)
        }
    }

    const handleDateClick = (date: Date) => {
        if (onSlotClick) {
            onSlotClick(date)
        }
    }

    const handleNavigate = (direction: 'prev' | 'next') => {
        // A data NOVA é calculada aqui, não lida de `calendar.currentDate`.
        //
        // `goToPrevious`/`goToNext` agendam a atualização do estado; nesta mesma
        // execução `calendar.currentDate` ainda é o valor do render atual. Avisar
        // o pai com ele mandava sempre a data ANTERIOR à navegação — a tela
        // mostrava setembro e o pai continuava em agosto.
        //
        // Passou despercebido enquanto a agenda baixava todos os eventos e
        // filtrava no cliente: a data do pai não mudava nada. Depois que a busca
        // passou a recortar por período, virou "o mês novo aparece vazio".
        const nextDate = navigateDate(calendar.currentDate, calendar.view, direction)

        if (direction === 'prev') {
            calendar.goToPrevious()
        } else {
            calendar.goToNext()
        }
        if (onDateChange) {
            onDateChange(nextDate)
        }
    }

    const handleToday = () => {
        calendar.goToToday()
        if (onDateChange) {
            onDateChange(new Date())
        }
    }

    const currentDayForDayView = useMemo(() => {
        return {
            date: calendar.currentDate,
            isCurrentMonth: true,
            isToday:
                calendar.currentDate.toDateString() ===
                new Date().toDateString(),
            isPast: calendar.currentDate < new Date(new Date().setHours(0, 0, 0, 0)),
            dayNumber: calendar.currentDate.getDate(),
        }
    }, [calendar.currentDate])

    return (
        <div className="flex flex-col h-full bg-base-100 rounded-xl border border-base-300/50 overflow-hidden">
            <div className="p-4 border-b border-base-300">
                <CalendarHeader
                    title={calendar.headerTitle}
                    view={calendar.view}
                    onPrevious={() => handleNavigate('prev')}
                    onNext={() => handleNavigate('next')}
                    onToday={handleToday}
                    onViewChange={handleViewChange}
                />
            </div>

            <div className="flex-1 overflow-hidden">
                {calendar.view === 'month' && (
                    <MonthView
                        days={calendar.days}
                        events={events}
                        onDateClick={handleDateClick}
                        onEventClick={onEventClick}
                    />
                )}

                {calendar.view === 'week' && (
                    <WeekView
                        days={getWeekDays(calendar.currentDate)}
                        events={events}
                        workingHours={workingHours}
                        slotDuration={slotDuration}
                        onSlotClick={onSlotClick}
                        onEventClick={onEventClick}
                    />
                )}

                {calendar.view === 'day' && (
                    <DayView
                        day={currentDayForDayView}
                        events={events}
                        workingHours={workingHours}
                        slotDuration={slotDuration}
                        onSlotClick={onSlotClick}
                        onEventClick={onEventClick}
                    />
                )}
            </div>
        </div>
    )
}

export { CalendarHeader } from './CalendarHeader'
export { MonthView } from './MonthView'
export { WeekView } from './WeekView'
export { DayView } from './DayView'
export { TimeSlot, TimeColumn } from './TimeSlot'
